/**
 * A redirect that keeps everything the old link carried (DOP-478).
 *
 * `<Navigate to="/workflows" />` throws the query string away, and every one
 * of the paths the unified IA retires carries state in it — the composer a
 * `/templates?compose=…` link opens, the thread and grain a `/workflow?…` link
 * opens the rail on. A bookmark that survives the move but loses what it was
 * pointing AT is only half a redirect.
 *
 * `to` may name route params (`/workflows/monitoring/:templateId`); they are
 * filled from the params of the route this redirect is mounted on.
 */

import React from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";

export interface RedirectToPathProps {
	/** Target path. `:name` segments are filled from the current route params. */
	to: string;
	/**
	 * Rewrites the old query into the new one. Omit to carry it over unchanged.
	 * Given a copy, so an implementation may mutate and return it.
	 */
	mapSearch?: (search: URLSearchParams) => URLSearchParams;
}

const RedirectToPath: React.FC<RedirectToPathProps> = ({ to, mapSearch }) => {
	const params = useParams();
	const [searchParams] = useSearchParams();

	const path = to.replace(/:([A-Za-z0-9_]+)/g, (whole, name: string) => {
		const value = params[name];
		return value === undefined ? whole : encodeURIComponent(value);
	});
	const search = (
		mapSearch
			? mapSearch(new URLSearchParams(searchParams))
			: new URLSearchParams(searchParams)
	).toString();

	// `replace`: the retired path must not sit in the history for Back to
	// bounce off, which would trap the user in the redirect.
	return <Navigate to={search ? `${path}?${search}` : path} replace />;
};

export default RedirectToPath;
